import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, NavLink } from "react-router-dom";
import { Bell, Home, Menu, PawPrint, CalendarDays, ListChecks, Wallet, Images, ShoppingBasket, Trophy, Gamepad2, MessageCircleHeart, Settings, LogOut, MessageSquarePlus, Heart, Gift } from "lucide-react";
import { useAuth } from "../lib/auth";
import { UserLogo } from "./Logos";
import { Sheet } from "./kit";
import { useGet } from "../lib/api";
import { SecretPopup } from "./SecretPopup";

function FanHeart() {
  const nav = useNavigate();
  const q = useGet("/fan/pending", { interval: 10000 });
  const n = q.data?.length || 0;
  return (
    <button className="fan-heart" onClick={() => nav("/fan")} data-testid="floating-heart" aria-label="Fan Notes">
      <Heart size={17} fill="#FF85A1" />
      {n > 0 && <span className="bell-badge" data-testid="floating-heart-count">{n}</span>}
    </button>
  );
}

export const NAV = [
  ["/home", "Home", Home], ["/calendar", "Calendar", CalendarDays], ["/chores", "Chores", ListChecks], ["/finances", "Finances", Wallet],
  ["/sparrow", "Sparrow", PawPrint], ["/gallery", "Gallery", Images], ["/shopping", "Shopping", ShoppingBasket], ["/wishlist", "Wishlist", Gift], ["/scoreboard", "Scoreboard", Trophy],
  ["/arcade", "Arcade", Gamepad2], ["/fan", "Fan Notes", MessageCircleHeart], ["/notifications", "Notifications", Bell], ["/settings", "Settings", Settings], ["/feedback", "Send Feedback", MessageSquarePlus],
];

function useEdgeSwipe(onBack) {
  useEffect(() => {
    let sx = null, sy = 0;
    const start = (e) => {
      const p = e.touches[0];
      sx = p.clientX < 24 && !e.target.closest("[data-no-swipe]") && !document.querySelector("[data-testid$='sheet'], [data-testid='confirm-dialog'], [data-testid='fan-gate']") ? p.clientX : null;
      sy = p.clientY;
    };
    const end = (e) => {
      if (sx == null) return;
      const p = e.changedTouches[0];
      if (p.clientX - sx > 70 && Math.abs(p.clientY - sy) < 60) onBack();
      sx = null;
    };
    document.addEventListener("touchstart", start, { passive: true });
    document.addEventListener("touchend", end, { passive: true });
    return () => { document.removeEventListener("touchstart", start); document.removeEventListener("touchend", end); };
  }, [onBack]);
}

function Header({ onMenu }) {
  const { me } = useAuth();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-30 glass pt-safe" data-testid="app-header">
      <div className="max-w-md mx-auto flex items-center justify-between px-4 h-14">
        <button onClick={() => nav("/home")} data-testid="header-account-logo" aria-label="Home"><UserLogo id={me.id} size={34} /></button>
        <div className="flex items-center gap-1">
          <button className="icon-btn sparrow-glow" onClick={() => nav("/sparrow")} data-testid="header-sparrow-shortcut" aria-label="Sparrow"><PawPrint size={18} /></button>
          <button className="icon-btn relative" onClick={() => nav("/notifications")} data-testid="header-notification-bell" aria-label="Notifications">
            <Bell size={18} />
            {me.unread > 0 && <span className="bell-badge" data-testid="header-unread-count">{me.unread > 99 ? "99+" : me.unread}</span>}
          </button>
          <button className="icon-btn" onClick={onMenu} data-testid="header-menu-button" aria-label="Menu"><Menu size={18} /></button>
        </div>
      </div>
    </header>
  );
}

export default function Shell() {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menu, setMenu] = useState(false);
  const back = useCallback(() => {
    if (loc.pathname === "/home") return;
    if (window.history.state?.idx > 0) nav(-1); else nav("/home");
  }, [loc.pathname, nav]);
  useEdgeSwipe(back);
  useEffect(() => { setMenu(false); window.scrollTo(0, 0); }, [loc.pathname]);
  return (
    <div className="min-h-screen">
      <Header onMenu={() => setMenu(true)} />
      {loc.pathname !== "/fan" && <FanHeart />}
      <main className={`max-w-md mx-auto px-4 pt-5 pb-28 ${loc.pathname.startsWith("/sparrow") ? "sparrow-theme" : ""}`}>
        <Outlet />
        <SecretPopup />
      </main>
      <Sheet open={menu} onClose={() => setMenu(false)} title="Our Home" testid="menu-sheet">
        <nav className="grid grid-cols-3 gap-2">
          {NAV.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} className={({ isActive }) => `nav-tile ${isActive ? "nav-tile-on" : ""}`} data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}>
              <Icon size={20} strokeWidth={1.6} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <button className="btn btn-ghost w-full mt-5 flex items-center justify-center gap-2" onClick={logout} data-testid="menu-sign-out"><LogOut size={16} /> Sign out of {me.display_name}</button>
      </Sheet>
    </div>
  );
}
